import type { SupplierSubmission } from './types';

/**
 * How the Send Samples feed is arranged.
 *
 * The first complaint this answered: every submission a supplier had ever sent
 * was listed in one flat column, so a run finished in June sat above the one
 * they were being asked to fix today, at the same size, with the same four
 * component cards expanded underneath it. Approved runs left the feed, and
 * what remained was grouped by whose move it was.
 *
 * The second complaint is what this file now answers. Grouping purely by state
 * puts everything a factory has ever sent and not yet had approved into one
 * heading called "With TAP", which by run thirty is the same wall in different
 * clothes. So the history reads chronologically instead — August 2026, July
 * 2026 — and exactly one group is exempt: the runs TAP has sent back.
 *
 * That group is pinned above the months rather than filed inside them, because
 * a revision requested in July is still the most urgent thing on the page in
 * August. It is also removed from its month, so no run is ever listed twice.
 *
 * Grouping by CG order was the original request and is not possible today:
 * supplier_submissions has no order column, and a supplier could not fill one
 * in anyway — they do not know our internal order numbers.
 */

/** Finished. Shown under Completed, never in the active feed. */
export const ARCHIVED_STATUS = 'approved';

/** The one state that earns a pin above the chronology. */
export const ACTION_STATUS = 'revision_requested';

export type SubmissionGroupKind = 'action' | 'month';

export interface SubmissionGroup {
  /** 'action', or the month key '2026-08'. Stable enough to use as a React key. */
  key: string;
  kind: SubmissionGroupKind;
  /** 'Needs your action', or 'August 2026'. */
  name: string;
  /** One line under the heading. Empty for month buckets, which explain themselves. */
  hint: string;
  submissions: SupplierSubmission[];
  /**
   * Whether the accordion starts open. What the supplier owes is always open;
   * of the months, only the one being lived in. An empty group is never
   * returned at all, so this only matters where there is something inside.
   */
  openByDefault: boolean;
}

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/**
 * The bucket a timestamp belongs to, as 'YYYY-MM'.
 *
 * Sliced off the ISO string rather than read from a Date, deliberately.
 * created_at is stored and returned in UTC; new Date(...).getMonth() would
 * answer in the reader's own zone, so a run sent at 23:30 UTC on the 31st
 * would land in the previous month for everyone west of Greenwich and the
 * right one for everyone east of it. Same string in, same bucket out.
 */
export function monthKey(iso: string): string {
  return iso.slice(0, 7);
}

/**
 * 'August 2026' from '2026-08'.
 *
 * Spelled out rather than localised. toLocaleDateString would hand back a
 * different string on a French machine and a differently-abbreviated one under
 * a different Node ICU build, which makes a heading nobody can write a test
 * against. Falls back to the key itself if the month is out of range, so a bad
 * timestamp shows something odd rather than the word "undefined".
 */
export function monthLabel(key: string): string {
  const name = MONTH_NAMES[Number(key.slice(5, 7)) - 1];
  return name === undefined ? key : `${name} ${key.slice(0, 4)}`;
}

function newestFirst(list: SupplierSubmission[]): SupplierSubmission[] {
  return list.slice().sort((left, right) => right.created_at.localeCompare(left.created_at));
}

/** Everything still in play. Approved runs are gone from here by design. */
export function activeSubmissions(all: SupplierSubmission[]): SupplierSubmission[] {
  return all.filter((submission) => submission.status !== ARCHIVED_STATUS);
}

/** The archive: approved runs only, newest first. */
export function completedSubmissions(all: SupplierSubmission[]): SupplierSubmission[] {
  return newestFirst(all.filter((submission) => submission.status === ARCHIVED_STATUS));
}

/**
 * Any list of runs, as month buckets, newest month first.
 *
 * Shared by the active feed and the Completed archive so the two tabs read the
 * same way. todayIso is passed in rather than read from the clock: a lens that
 * consults the current time cannot be tested at a month boundary, which is the
 * only place its behaviour is interesting.
 */
export function groupByMonth(list: SupplierSubmission[], todayIso: string): SubmissionGroup[] {
  const buckets = new Map<string, SupplierSubmission[]>();
  for (const submission of list) {
    const key = monthKey(submission.created_at);
    const bucket = buckets.get(key);
    if (bucket === undefined) {
      buckets.set(key, [submission]);
    } else {
      bucket.push(submission);
    }
  }

  const keys = [...buckets.keys()].sort((left, right) => right.localeCompare(left));
  const current = monthKey(todayIso);
  // The current month, or failing that the newest one there is. A supplier who
  // last sent something in June must not open the tab to a column of shut
  // headings and no content — that reads as a page that failed to load.
  const openKey = keys.includes(current) ? current : keys[0];

  return keys.map((key) => ({
    key,
    kind: 'month' as const,
    name: monthLabel(key),
    hint: '',
    submissions: newestFirst(buckets.get(key) ?? []),
    openByDefault: key === openKey,
  }));
}

/**
 * The active feed: what the supplier owes, pinned, then the months.
 *
 * Empty groups are never returned — headings over an empty page read as
 * breakage, not tidiness.
 */
export function groupSubmissions(all: SupplierSubmission[], todayIso: string): SubmissionGroup[] {
  const active = activeSubmissions(all);
  const owed = active.filter((submission) => submission.status === ACTION_STATUS);
  const rest = active.filter((submission) => submission.status !== ACTION_STATUS);

  const groups: SubmissionGroup[] = [];
  if (owed.length > 0) {
    groups.push({
      key: 'action',
      kind: 'action',
      name: 'Needs your action',
      hint: 'TAP sent these back. Upload new photos for the components asking for them.',
      submissions: newestFirst(owed),
      openByDefault: true,
    });
  }
  groups.push(...groupByMonth(rest, todayIso));
  return groups;
}
